<template>
  <component
    :is="element"
    class="product-card"
    :class="{
      [$class('loading')]: !product,
      'show-secondary-image': showSecondaryImage,
    }"
    :href="localProduct.url"
  >
    <component
      :is="liquid?.enableColourSwatch || showDynamicSwatches ? 'a' : 'div'"
      class="product-card__image-container"
      :href="localProduct.url"
    >
      <responsive-image
        :image="productImage"
        class="product-card__image product-card__primary-image"
        min-max="1200-2400"
        :ratio="0.86"
        :sizes="sizes"
      />

      <responsive-image
        v-if="showSecondaryImage && localProduct.secondaryImage"
        class="product-card__secondary-image"
        :image="localProduct.secondaryImage"
        min-max="1200-2400"
        :padding="false"
        :sizes="sizes"
      />

      <span
        v-if="productBadge"
        class="
          product-card__badge
          text-other-label-s
          text-other-label-l-desktop
        "
        v-text="productBadge"
      />
    </component>

    <div class="product-card__footer">
      <div class="product-card__footer-content">
        <div class="product-card__footer-content--header">
          <div
            v-if="starRatingInstanceId"
            class="product-card__reviews"
          >
            <div
              class="yotpo-star-rating-widget yotpo-widget-instance"
              :data-yotpo-instance-id="starRatingInstanceId"
              :data-yotpo-product-id="localProduct.id"
              data-yotpo-section-id="collection"
              js-yotpo-star-rating="container"
            ></div>
          </div>

          <p
            class="
              product-card__title
              product-card__title--short
              text-body-s-bold
              text-body-m-bold-desktop
            "
            v-html="getProcessedTitle"
          />

          <p
            v-if="getSubtitle"
            class="
              product-card__subtitle
              text-body-xxs-regular
              text-body-xs-regular-desktop
            "
            v-text="getSubtitle"
          />

          <p
            class="
              product-card__type
              text-body-xxs-bold
              text-body-xs-bold-desktop
            "
            v-html="localProduct.type"
          />
        </div>

        <div
          class="product-card__footer-content--body"
          :class="{
            'product-card__footer--wide': localProduct?.price?.varies && !showDynamicSwatches,
          }"
        >
          <product-prices
            class="product-card__prices"
            :available="localProduct?.available"
            :compare-at-price="localProduct?.compareAtPrice?.amount"
            :price="localProduct?.price?.amount"
            :varies="localProduct?.price?.varies"
          />

          <div
            v-if="
              collectionPage
              && localProduct.relatedColoursCount > 0
              && !showQuickAdd
            "
            class="product-card__footer-content--colors"
          >
            <colour-swatch
              v-if="!showDynamicSwatches"
              :liquid="liquid"
              :product="localProduct"
              :colours="localColours"
              :show-colors="true"
              location="colour-swatch--product-colour-selector"
              @update-colour-swatch="querySelectedColourSwatchProduct"
            />

            <p
              v-if="localProduct.relatedColoursCount > 0 && collectionPage"
              class="
                product-card__colours
                text-body-xxs-bold
                text-body-xs-bold-desktop
              "
              v-text="$string(
                'product.colours',
                {
                  replace: { num: localProduct.relatedColoursCount},
                  pluralise: localProduct.relatedColoursCount,
                }
              )"
            />
          </div>

        </div>
      </div>

      <div
        v-if="showDynamicSwatches === true || showAddToCart === true || liquid.enableColourSwatch"
        class="product-card__swatch-add-to-cart-container"
      >
        <colour-swatch
          :liquid="liquid"
          :product="localProduct"
          :colours="localColours"
          :show-colors="showDynamicSwatches"
          :show-all-colours="collectionPage ? true : false"
          @update-colour-swatch="querySelectedColourSwatchProduct"
        />

        <add-to-cart
          v-if="!collectionPage"
          :product="localProduct"
        />
      </div>
    </div>
  </component>
</template>

<script>

/**
 * Vue: Product card
 * -----------------------------------------------------------------------------
 * Product card used throughout the site.
 *
 * @param {Boolean|Object} product - Product object, false shows loading state.
 * @param {Boolean} showCollectionImage - Condition to display image for
 * collection page.
 * @param {Boolean} showSecondaryImage - Condition to display secondary image
 * on hover.
 *
 */
import AddToCart from '~async/product-card/add-to-cart/add-to-cart'
import ColourSwatch from '~async/product-card/colour-swatch/colour-swatch'
import ProductPrices from '~global/product-prices/product-prices'
import ResponsiveImage from '~global/images/responsive-image'

import { formatProduct } from '~/helpers/format'
import productQuery from '~graphql/product.gql'
import { mapState } from 'vuex'

export default {
  name: 'ProductCard',

  components: {
    AddToCart,
    ColourSwatch,
    ProductPrices,
    ResponsiveImage,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        productId: 0,
        sectionId: '',
        title: '',
        enableColourSwatch: false,
      }),
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    productSubtitles: {
      type: Array,
      default: false,
      required: true,
    },
    showCollectionImage: {
      type: Boolean,
      default: false,
    },
    showSecondaryImage: {
      type: Boolean,
      default: false,
    },
    collectionPage: {
      type: Boolean,
      default: false,
    },
    starRatingInstanceId: {
      type: Boolean,
      default: false,
    },
    mobile: {
      type: Boolean,
      default: false,
    },
    showAddToCart: {
      type: Boolean,
      default: false,
    },
    showDynamicSwatches: {
      type: Boolean,
      default: false,
    },
    sizes: {
      type: [String, null],
      default: 'auto',
    },
  },

  emits: {
    'update-product-card': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      localProduct: {},
      localColours: [],
    }
  },

  watch: {
    product(newData) {
      this.localProduct = newData
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
     ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Remove dryrobe from product title.
     */
    getProcessedTitle() {
      return this.localProduct.title?.replace('dryrobe', '') || '';
    },

    /**
     * Return product subtitle.
     */
    getSubtitle() {
      if (!this.localProduct || !this.productSubtitles) return false;

      const matchedSubtitle = this.productSubtitles.find(subtitle =>
        subtitle.productType === this.localProduct.type
      );

      return matchedSubtitle ? matchedSubtitle.productSubtitle : false;
    },

    /**
     * Compute element to 'div' or 'a'.
     */
    element() {
      if (this.liquid?.enableColourSwatch || this.showDynamicSwatches) {
        return 'div'
      }

      return 'a'
    },

    /**
     * Compute product badge from tags
     * @return {Boolean | String}
     */
    productBadge() {
      const containsBadge = this.localProduct?.tags?.find(tag => tag.includes('badge:'))

      if (!containsBadge) {
        return false
      }

      const badge = containsBadge.replace('badge:', '').trim()
      return badge
    },

    /**
     * Compute product image
     * @return {Object}
     */
    productImage() {
      if (this.showCollectionImage && this.localProduct?.collectionImage?.src?.length) {
        return this.localProduct.collectionImage
      }

      return this.localProduct.featuredImage
    },
  },


  watch: {

    /**
     * Watch mobile for update to load new product.
     */
     mobile() {
      if (this.localProduct.id && this.mobile) {
        yotpoWidgetsContainer.initWidgets()
      }
    }
  },

  mounted() {
    this.localColours = this.product?.relatedCollection
    this.localProduct = this.product

    this.localColours = this.localColours?.sort((item) => {
      return item?.handle === this.localProduct?.handle ? -1 : 0
    })

    if (this.localProduct.id && this.mobile) {
      yotpoWidgetsContainer.initWidgets()
    }
  },

  methods: {

    /**
     * Query selected colour swatch product
     */
    async querySelectedColourSwatchProduct(handle) {
      try {
        const response = await cnvs.Query({
          query: productQuery,
          variables: {
            country: cnvs.store.country,
            handle,
            language: cnvs.store.language,
          },
        })

        this.localProduct = formatProduct(response)

      } catch (error) {
        cnvs.ReportError('Failed to perform search', error)
      } finally {
        this.state = 'ready'
      }
    },
  },
}
</script>

<style lang="scss">
@import './product-card';
</style>
